<template>
  <div class="question">
    <div class="question__message">
      <RichTextRenderer tag="div" class="question__message-answer" :html="feedbackMsg" />

      <div v-if="!feedback.redirectToNextQuestion" class="content">
        <div class="content__item content__item--action">
          <UIButton :id="'game-button-message'" :loading="isLoading" @click="onButtonPress">
            <span class="content__item-action-text">{{ getButtonLabel }}</span>
          </UIButton>
          <div v-if="canNavigateBack" class="question__back">
            <a @click.prevent="$emit('previousQuestion')">
              {{ applyReplacementTags(previousButtonLabel) }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent, onBeforeMount, ref } from 'vue';
import type { GameQuizQuestionFeedbackState } from '@/src/components/games/quiz/Model';
import { applyReplacementTags } from '@/src/utilities/Utilities';
import UIButton from '@/src/components/ui/UIButton.vue';
import { useCampaignStore } from '@/src/store/campaign';
import RichTextRenderer from '@/src/components/RichTextRenderer.vue';

export default defineComponent({
  name: 'QuizQuestionFeedback',
  components: { RichTextRenderer, UIButton },
  props: {
    hasCorrectAnswer: {
      type: Boolean,
      required: true
    },
    feedback: {
      type: Object as PropType<GameQuizQuestionFeedbackState>,
      required: true
    },
    isQuestionLast: {
      type: Boolean,
      required: true
    },
    previousButtonLabel: {
      type: String,
      default: 'Previous'
    },
    index: {
      type: Number,
      required: true
    },
    showBackgroundOverlay: {
      type: Boolean,
      default: false
    },
    /**
     * Exposes a button for the user to be able to navigate back to last question.
     */
    canNavigateBack: {
      type: Boolean,
      default: false
    }
  },
  emits: ['nextQuestion', 'previousQuestion'],
  setup(props, context) {
    let timer: number | undefined;
    const isLoading = ref(false);
    const campaignStore = useCampaignStore();

    const getButtonLabel = computed(() => {
      if (props.feedback.feedbackButtonLabel) {
        return applyReplacementTags(props.feedback.feedbackButtonLabel);
      }

      const lastButtonLabel = props.feedback.buttonLastLabel
        ? props.feedback.buttonLastLabel
        : props.feedback.buttonNextLabel;

      if (props.isQuestionLast && lastButtonLabel) {
        return applyReplacementTags(lastButtonLabel);
      }

      return applyReplacementTags(props.feedback.buttonNextLabel || 'Næste spørgsmål');
    });

    const feedbackMsg = computed(() => {
      if (props.hasCorrectAnswer && props.feedback.answerMessage?.correct) {
        return applyReplacementTags(props.feedback.answerMessage?.correct);
      } else if (props.feedback.answerMessage?.wrong) {
        return applyReplacementTags(props.feedback.answerMessage?.wrong);
      }

      return '';
    });

    const onButtonPress = () => {
      if (props.isQuestionLast) {
        isLoading.value = true;
      }
      context.emit('nextQuestion');
    };

    const redirectToNextQuestion = () => {
      if (props.feedback?.redirectToNextQuestion) {
        if (typeof timer === 'number') {
          window.clearTimeout(timer);
        }
        timer = window.setTimeout(() => {
          context.emit('nextQuestion');
        }, props.feedback.delayUntilNext);
      }
    };

    onBeforeMount(() => {
      if (!props.showBackgroundOverlay) {
        campaignStore.gameSectionImageOverlay = undefined;
      }
    });

    redirectToNextQuestion();

    return {
      feedbackMsg,
      onButtonPress,
      getButtonLabel,
      applyReplacementTags,
      isLoading
    };
  }
});
</script>
